import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeUpOnScroll } from './ScrollReveal';
import { GradientText } from './AnimatedText';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Marco Rossi',
    role: 'Inhaber',
    business: 'Pizzeria Napoli, München',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    quote: 'Cash-X hat unseren Betrieb revolutioniert. Die automatische TSE-Signierung gibt mir Sicherheit, und das Küchendisplay spart uns jeden Tag Zeit. Absolute Empfehlung!',
    rating: 5,
    highlight: '+35% Effizienz'
  },
  {
    id: 2,
    name: 'Sarah Müller',
    role: 'Geschäftsführerin',
    business: 'Café Sonnenschein, Berlin',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    quote: 'Endlich ein Kassensystem, das einfach funktioniert! Die Einrichtung dauerte keine 10 Minuten, und der Support ist wirklich 24/7 erreichbar. Genau das, was wir gesucht haben.',
    rating: 5,
    highlight: 'Setup in 10 Min'
  },
  {
    id: 3,
    name: 'Thomas Weber',
    role: 'Restaurantleiter',
    business: 'Gasthof zum Löwen, Stuttgart',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    quote: 'Die Integration mit Lieferando und Wolt war ein Gamechanger. Alle Bestellungen in einem System – keine doppelte Eingabe mehr. Unsere Fehlerquote ist auf praktisch null gesunken.',
    rating: 5,
    highlight: '0% Fehlerquote'
  },
  {
    id: 4,
    name: 'Lisa Schmidt',
    role: 'Inhaberin',
    business: 'Asia Wok Express, Hamburg',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    quote: 'Als Lieferdienst war die Fahrerverwaltung für uns entscheidend. Cash-X bietet alles: Routenplanung, automatische Zuordnung und Live-Tracking. Meine Fahrer lieben es!',
    rating: 5,
    highlight: 'Fahrer-Management'
  },
  {
    id: 5,
    name: 'Ahmed Hassan',
    role: 'Geschäftsführer',
    business: 'Doner King, Köln',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
    quote: 'Der KI-Import hat meine komplette Speisekarte in 5 Minuten eingelesen – mit Fotos! Das hätte früher Tage gedauert. Cash-X denkt wirklich mit.',
    rating: 5,
    highlight: 'KI-Import'
  }
];

export const TestimonialsSectionUltimate = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => Math.abs(offset) * velocity;

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prev) => {
      let next = prev + newDirection;
      if (next < 0) next = testimonials.length - 1;
      if (next >= testimonials.length) next = 0;
      return next;
    });
  };

  // Auto-rotate
  useEffect(() => {
    const timer = setInterval(() => paginate(1), 6000);
    return () => clearInterval(timer);
  }, []);

  const current = testimonials[currentIndex];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ backgroundColor: '#0B1220' }}
    >
      {/* Background Glow */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(ellipse 60% 40% at 50% 100%, rgba(45,212,191,0.06), transparent)'
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <FadeUpOnScroll className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 mb-6">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">4.9/5 Durchschnittsbewertung</span>
          </div>
          <h2 
            data-testid="testimonials-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Das sagen unsere <GradientText from="#F59E0B" to="#FBBF24">Kunden</GradientText>
          </h2>
          <p className="mt-6 text-lg sm:text-xl max-w-2xl mx-auto text-slate-400">
            Über 500 Gastronomen vertrauen auf Cash-X für ihren täglichen Betrieb.
          </p>
        </FadeUpOnScroll>

        {/* Main Testimonial Carousel */}
        <div className="relative">
          {/* Quote Icon */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
            <div className="w-12 h-12 bg-[#111C32] border border-slate-700/50 rounded-full flex items-center justify-center">
              <Quote className="w-5 h-5 text-amber-400" />
            </div>
          </div>

          {/* Carousel Container */}
          <div className="relative h-[400px] md:h-[350px] overflow-hidden">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.3 },
                  scale: { duration: 0.3 }
                }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                onDragEnd={(e, { offset, velocity }) => {
                  const swipe = swipePower(offset.x, velocity.x);
                  if (swipe < -swipeConfidenceThreshold) paginate(1);
                  else if (swipe > swipeConfidenceThreshold) paginate(-1);
                }}
                className="absolute inset-0 flex items-center justify-center px-4"
              >
                <div 
                  className="w-full max-w-3xl bg-[#111C32]/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 md:p-12"
                  style={{ boxShadow: '0 20px 60px -10px rgba(0,0,0,0.5)' }}
                >
                  {/* Rating */}
                  <div className="flex justify-center gap-1 mb-6">
                    {[...Array(current.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="text-lg md:text-xl text-slate-200 text-center leading-relaxed mb-8">
                    "{current.quote}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center justify-center gap-4">
                    <img
                      src={current.image}
                      alt={current.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-slate-600"
                    />
                    <div className="text-left">
                      <p className="font-semibold text-white">{current.name}</p>
                      <p className="text-sm text-slate-400">{current.role}, {current.business}</p>
                    </div>
                    {current.highlight && (
                      <div className="hidden sm:block ml-4 px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
                        <span className="text-sm text-emerald-400 font-medium">{current.highlight}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => paginate(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#111C32]/80 border border-slate-700/50 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-600 transition-colors z-20"
            aria-label="Vorheriges Testimonial"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => paginate(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#111C32]/80 border border-slate-700/50 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-600 transition-colors z-20"
            aria-label="Nächstes Testimonial"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setDirection(i > currentIndex ? 1 : -1);
                setCurrentIndex(i);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentIndex 
                  ? 'w-8 bg-amber-400' 
                  : 'bg-slate-600 hover:bg-slate-500'
              }`}
              aria-label={`Gehe zu Testimonial ${i + 1}`}
            />
          ))}
        </div>

        {/* Additional Mini Testimonials */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.slice(0, 3).map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="p-6 bg-[#111C32]/60 border border-slate-700/40 rounded-xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-white">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.business.split(',')[0]}</p>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-3 h-3 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
              <p className="text-sm text-slate-400 line-clamp-3">
                "{t.quote.slice(0, 120)}..."
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSectionUltimate;
